// The page's half of the native photo and video upload (BL-2683, BL-2684, ADR 070 — Ingest Protocol § 7.6): hand a lot's
// queued photos and videos to the native transfer queue, then read back which rows finished. The foreground sender
// (`lot-media-sender.ts`) stays the executor for everything native does not take.
import { stagedPosterIdOf, stagedThumbIdOf } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-attachments';
import { ingestPlacement, markAddsNative, mediaOf, withoutAdd, type MediaAdd } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-state-media';
import { planMediaHandoff } from '@auctogon/admin-isomorphic/isomorphic/offline/native-media-handoff';
import type { LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import { LOT_MEDIA_INGEST_LIMITS } from '@auctogon/core-admin/schemas/lot-attachments';
import { makeSendThumbnail } from '../../api-client/send-thumbnail';
import type { NativeTransferItem, NativeTransferQueue, OfflineMediaStaging, OfflineStore } from '../../gateways/native-device';
import { toMediaUploadItem } from '../../gateways/native-device/native-transfer/lanes';
import { NATIVE_TRANSFER_MIN_ENGINE_VERSION } from '../../gateways/native-device/native-transfer/plugin-methods';
import type { ReplayTransport } from './replay-transport';

/** What the hand-off needs from the replay that owns it. */
export interface LotMediaNativeDeps {
  store: OfflineStore;
  media?: OfflineMediaStaging | null;
  native: NativeTransferQueue;
  /** Reads the lot, for the inventory item its signed links name. */
  transport: Pick<ReplayTransport, 'readLot'>;
  notify: () => void;
  logError?: (message: string, err: Error) => void;
}

const asError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

/** The text a row carries, captured now (ADR 066 — Offline Mode § 8). */
function rowLabel(state: LotDesiredState, add: MediaAdd): string {
  const { lotNumber, title } = state.label;
  const noun = add.type === 'video' ? 'Video' : 'Photo';
  return lotNumber != null && lotNumber !== '' ? `${noun} for lot ${lotNumber}, ${title}` : `${noun} for ${title}`;
}

/** Resolves the lot's inventory item id once per hand-off, or null (already logged) when it cannot be read. */
type ItemIdOf = () => Promise<string | null>;

const ITEM_UNREADABLE = 'change queue: the lot’s inventory item could not be read for the native lane';

/** The lot's inventory item id, which `POST /api/files/upload-url` keys `inventory` uploads on (the lot id is refused). */
async function readItemId(deps: LotMediaNativeDeps, input: { lotId: string; signal: AbortSignal }): Promise<string | null> {
  try {
    const answer = await deps.transport.readLot({ lotId: input.lotId, signal: input.signal });
    const body = answer.body as { success?: boolean; data?: { inventoryItemId?: string | null } } | null;
    const itemId = answer.status === 200 && body?.success === true ? body.data?.inventoryItemId : null;
    if (itemId) return itemId;
    deps.logError?.(ITEM_UNREADABLE, new Error(`the lot read answered ${answer.status} with no inventory item`));
  } catch (err) {
    deps.logError?.(ITEM_UNREADABLE, asError(err));
  }
  return null;
}

/**
 * The staged thumbnail a video's row carries: the one a failed earlier pass already made, or one derived now from the
 * staged poster frame (the native lane has no canvas, and the server wants a JPEG of at most 100 KiB).
 * @returns the staged thumbnail's id, or null (logged) when it cannot be made.
 * @sideEffect Writes the thumbnail to the device, owned by the video's entry so the sweep and purge reach it.
 */
async function ensureVideoThumbnail(deps: LotMediaNativeDeps, add: MediaAdd): Promise<string | null> {
  const { media } = deps;
  if (!media || add.stagedFileId == null || add.stagedPosterFileId == null) return null;
  const thumbId = stagedThumbIdOf(add.stagedFileId);
  try {
    if ((await media.get(thumbId)) != null) return thumbId;
    const poster = await media.read(add.stagedPosterFileId);
    if (!poster) return null;
    const blob = await makeSendThumbnail(poster.blob);
    const staged = await media.stage({ id: thumbId, ownerEntryId: add.stagedFileId, name: `${thumbId}.jpg`, blob });
    if (staged.success || staged.code === 'DUPLICATE_ID') return thumbId;
    deps.logError?.('change queue: a video preview could not be made for the native lane', new Error(`staging the preview answered ${staged.code}`));
  } catch (err) {
    deps.logError?.('change queue: a video preview could not be made for the native lane', asError(err));
  }
  return null;
}

/** The queue item for one photo or video, or null when its staged files are gone or it cannot ride the lane. */
async function itemFor(deps: LotMediaNativeDeps, input: { state: LotDesiredState; add: MediaAdd; itemIdOf: ItemIdOf }): Promise<NativeTransferItem | null> {
  const { add, state } = input;
  if (add.stagedFileId == null) return null;
  const staged = await deps.media?.get(add.stagedFileId);
  if (!staged) return null;
  const isVideo = add.type === 'video';
  // A video always runs the signed-link chain, and a photo over the threshold does: both name the lot's inventory item.
  const needsItem = isVideo || staged.size > LOT_MEDIA_INGEST_LIMITS.nativeThresholdBytes;
  const itemId = needsItem ? await input.itemIdOf() : null;
  if (needsItem && itemId == null) return null;
  const thumbStagedId = isVideo ? await ensureVideoThumbnail(deps, add) : stagedPosterIdOf(add.stagedFileId);
  if (thumbStagedId == null) return null;
  const { position, isCover } = ingestPlacement(mediaOf(state), add);
  try {
    return toMediaUploadItem({
      label: rowLabel(state, add),
      payload: {
        kind: isVideo ? 'video' : 'photo',
        lotId: state.lotId,
        mediaId: add.id,
        photoStagedId: add.stagedFileId,
        thumbStagedId,
        contentType: staged.mimeType,
        position,
        isCover,
        ...(itemId != null ? { linkEntityType: 'inventory', linkEntityId: itemId } : {}),
      },
    });
  } catch (err) {
    // An id the lane cannot carry (not a v4 UUID): the foreground sends it.
    deps.logError?.('change queue: a photo or video cannot ride the native lane', asError(err));
    return null;
  }
}

/** Enqueue one item. @returns true when native holds the row afterwards (new, or already there). */
async function enqueueOne(deps: LotMediaNativeDeps, item: NativeTransferItem): Promise<boolean> {
  try {
    const result = await deps.native.enqueue({ items: [item] });
    if (result.success || result.code === 'DUPLICATE_ID') return true;
    // An unlisted lane or no active scope is the expected "native cannot take it": the foreground sends it.
    if (result.code === 'INVALID_ITEM') deps.logError?.('change queue: native refused a photo or video', new Error(result.error));
    return false;
  } catch (err) {
    deps.logError?.('change queue: the native queue could not take a photo or video', asError(err));
    return false;
  }
}

/**
 * Hand a lot's eligible queued photos and videos to the native queue, one row each (row id = media id, so repeating it is
 * safe). One native does not take stays untouched for the foreground executor.
 *
 * @param deps - the store, staging, native queue and the lot read.
 * @param input.lotId - the lot, which must already exist on the server.
 * @param input.signal - aborts the lot read.
 * @returns once every eligible add has been tried.
 * @sideEffect Enqueues native rows; stages a thumbnail beside each handed-off video; marks the handed-off adds `native` in the lot's state.
 */
export async function handOffMedia(deps: LotMediaNativeDeps, input: { lotId: string; signal: AbortSignal }): Promise<void> {
  const state = await deps.store.lotStates.get(input.lotId);
  if (!state || !deps.media) return;
  let lanes: string[];
  try {
    const caps = await deps.native.capabilities();
    // An engine without `getRow` could take the photo but never report back: the foreground keeps it.
    if (caps.engineVersion < NATIVE_TRANSFER_MIN_ENGINE_VERSION) return;
    lanes = caps.lanes;
  } catch (err) {
    deps.logError?.('change queue: the native queue could not be read', asError(err));
    return;
  }
  const media = mediaOf(state);
  const thumbnailsStaged = new Set<string>();
  for (const add of media.adds) {
    // A photo's preview is its pick-time thumbnail; a video's is the poster frame its pick staged.
    const preview = add.type === 'video' ? add.stagedPosterFileId : add.stagedFileId != null ? stagedPosterIdOf(add.stagedFileId) : undefined;
    if (add.stagedFileId != null && preview != null && (await deps.media.get(preview)) != null) thumbnailsStaged.add(add.stagedFileId);
  }
  const { handoff } = planMediaHandoff({ media, lanes, thumbnailsStaged });
  const handed: string[] = [];
  // One lot read serves every add that needs the item, and only a hand-off that has one pays for it.
  let itemId: Promise<string | null> | null = null;
  const itemIdOf: ItemIdOf = () => (itemId ??= readItemId(deps, { lotId: input.lotId, signal: input.signal }));
  for (const add of handoff) {
    const item = await itemFor(deps, { state, add, itemIdOf });
    if (item != null && (await enqueueOne(deps, item))) handed.push(add.id);
  }
  if (handed.length === 0) return;
  await deps.store.lotStates.update(input.lotId, (cur) => cur && { ...cur, media: markAddsNative(mediaOf(cur), handed) });
  deps.notify();
}

/** What reading one handed-off photo's row concluded. */
type RowVerdict = { add: MediaAdd; outcome: 'finished' | 'discarded' | 'waiting' };

/** Reads one handed-off photo's row by its id (row id = media id, ADR 070 § 1) and names what the lot does with it. */
async function verdictFor(deps: LotMediaNativeDeps, add: MediaAdd): Promise<RowVerdict> {
  try {
    const row = await deps.native.getRow({ id: add.id });
    if (row == null) {
      // Purged or scope-wiped under a lot that still names it: never read absence as finished.
      deps.logError?.('change queue: native holds no row for a handed-off photo', new Error(`no native row ${add.id}`));
      return { add, outcome: 'waiting' };
    }
    if (row.state === 'done') return { add, outcome: 'finished' };
    if (row.state === 'discarded') return { add, outcome: 'discarded' };
    return { add, outcome: 'waiting' };
  } catch (err) {
    deps.logError?.('change queue: the native queue could not be read', asError(err));
    return { add, outcome: 'waiting' };
  }
}

/**
 * Settle the lot's handed-off photos against their native rows, each read by id (BL-2951), so a row's age or the number
 * of rows around it cannot change the answer. A `done` row is finished: gone from the state, files untouched (native
 * released them). A `discarded` one also leaves the lot's order. A row native still holds, failed ones included, keeps
 * the lot waiting: a failed row is retried or discarded from the Storage & Sync page, which is native's own. A row
 * native does not hold, or a read that fails, keeps it waiting too and is logged.
 *
 * @param deps - the store and native queue.
 * @param input.lotId - the lot.
 * @returns whether any handed-off photo is still unfinished.
 * @sideEffect Removes finished adds from the lot's state.
 */
export async function reconcileNativeMedia(deps: LotMediaNativeDeps, input: { lotId: string }): Promise<{ waiting: boolean }> {
  const state = await deps.store.lotStates.get(input.lotId);
  const handed = state ? mediaOf(state).adds.filter((a) => a.native) : [];
  if (handed.length === 0) return { waiting: false };
  const verdicts = await Promise.all(handed.map((add) => verdictFor(deps, add)));
  const finished = verdicts.filter((v) => v.outcome !== 'waiting');
  if (finished.length > 0) {
    await deps.store.lotStates.update(input.lotId, (cur) => {
      if (!cur) return cur;
      let media = mediaOf(cur);
      for (const { add } of finished) media = withoutAdd(media, add.id);
      // A recorded order must not name a photo the server never received.
      const dropped = finished.filter((v) => v.outcome === 'discarded').map((v) => v.add.id);
      const order = media.order?.filter((id) => !dropped.includes(id)) ?? null;
      return { ...cur, media: { ...media, order: order != null && order.length > 0 ? order : null } };
    });
    deps.notify();
  }
  return { waiting: handed.length > finished.length };
}
